import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { SettingsCard } from '@/routes/settings/components/SettingsLayout';
import { RoutingSection } from '@/routes/settings/components/RoutingSection';
import { ActivityLogSection } from '@/routes/settings/components/ActivityLogSection';
import { usePermissions } from '@/contexts/PermissionContext';

interface AnalysisSetupRoutingSearch {
  tab?: 'log';
}

export const Route = createFileRoute('/analysis-setup/routing')({
  validateSearch: (search: Record<string, unknown>): AnalysisSetupRoutingSearch => ({
    tab: search.tab === 'log' ? 'log' : undefined,
  }),
  component: AnalysisSetupRoutingStep,
});

// Rules and the routing log as tabs of one section — same structure as the
// `routing` case in settings/index.tsx, moved 1:1 (RoutingSection stays in
// its original file; only the Tabs markup is replicated here).
function AnalysisSetupRoutingStep() {
  const { t } = useTranslation(['settings', 'common']);
  const { tab } = Route.useSearch();
  // The inbound log is integration operations (#1624): client, admin and the
  // platform owner. Others get the rules only — the tab is not offered and the
  // log is never mounted, so its request is never sent.
  const { canOperateIntegrations: canSeeLog } = usePermissions();
  // The URL is an entry point (as on /alerts): `?tab=` sets the choice, clicks
  // after that are local. The tab SHOWN is derived from the choice and the
  // permission, so a permission that resolves after mount opens a `?tab=log`
  // link, and one that is lost (org switch) falls back to the rules instead of
  // leaving Radix on a value with no trigger and no panel.
  const [chosenTab, setChosenTab] = useState<'rules' | 'log'>(tab ?? 'rules');
  useEffect(() => {
    setChosenTab(tab ?? 'rules');
  }, [tab]);
  const activeTab = chosenTab === 'log' && canSeeLog ? 'log' : 'rules';

  return (
    <div data-testid="step-routing">
      <Tabs
        value={activeTab}
        onValueChange={(value) => setChosenTab(value === 'log' ? 'log' : 'rules')}
      >
        <TabsList variant="underline" className="mb-4 px-0">
          <TabsTrigger value="rules" variant="underline" data-testid="routing-tab-rules">
            {t('routingTabs.rules', 'Rules')}
          </TabsTrigger>
          {canSeeLog && (
            <TabsTrigger value="log" variant="underline" data-testid="routing-tab-log">
              {t('routingTabs.log', 'Log')}
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="rules">
          <SettingsCard
            description={t(
              'routingDescription',
              'Configure how records are routed to projects'
            )}
            testId="section-routing"
          >
            <RoutingSection />
          </SettingsCard>
        </TabsContent>
        {canSeeLog && (
          <TabsContent value="log">
            <SettingsCard
              description={t(
                'routingLogDescription',
                'Monitor routing decisions and record assignments'
              )}
              testId="section-routing-log"
            >
              <ActivityLogSection />
            </SettingsCard>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
